/**
 * MMOL Animations
 * Intersection Observer-based animation triggers for fade-up and reveal-scale elements
 */

(() => {
  'use strict';

  // Animation configuration
  const ANIMATION_CONFIG = {
    threshold: 0.1, // Trigger when 10% of element is visible
    rootMargin: '0px 0px -50px 0px', // Start animation slightly before element enters viewport
  };

  // Check if user prefers reduced motion
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // If user prefers reduced motion, make all elements visible immediately
  if (prefersReducedMotion) {
    document.addEventListener('DOMContentLoaded', () => {
      const animatedElements = document.querySelectorAll('.fade-up, .reveal-scale');
      animatedElements.forEach(el => el.classList.add('visible'));
    });
    return; // Exit early
  }

  /**
   * Intersection Observer callback
   * Adds 'visible' class to elements when they enter the viewport
   */
  const handleIntersection = (entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Add visible class to trigger animation
        entry.target.classList.add('visible');

        // Optional: Unobserve after animation triggers (one-time animation)
        // Comment out the line below if you want animations to repeat on scroll
        observer.unobserve(entry.target);
      }
    });
  };

  /**
   * Auto-add animation classes to main page elements
   * This progressively enhances existing sections with animations
   */
  const autoAddAnimationClasses = () => {
    // Skip if animations already manually added
    if (document.querySelector('[data-mmol-animations-applied]')) {
      return;
    }

    // Select sections that should be animated on index/main pages
    const selectors = [
      '.section__blocks > .group',           // Group blocks in sections
      '.section__blocks > .text',            // Text blocks
      '.section__blocks > .button',          // Button blocks
      '.section__blocks > .image',           // Image blocks
      '.section__blocks > .icon',            // Icon blocks
      '.hero__content > *',                  // Hero content children
      '.product-list',                       // Product lists
      '.media-with-content',                 // Media with content sections
      '.footer__blocks > *',                 // Footer blocks
    ];

    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach((el, index) => {
        // Skip if already has animation class
        if (el.classList.contains('fade-up') || el.classList.contains('reveal-scale')) {
          return;
        }

        // Add fade-up class
        el.classList.add('fade-up');

        // Add staggered delay (max 4)
        const delayClass = `delay-${Math.min((index % 4) + 1, 4)}`;
        el.classList.add(delayClass);
      });
    });

    // Mark as applied
    document.documentElement.setAttribute('data-mmol-animations-applied', 'true');
  };

  /**
   * Initialize Intersection Observer
   */
  const initAnimationObserver = () => {
    // Auto-add animation classes first
    autoAddAnimationClasses();

    // Create observer
    const observer = new IntersectionObserver(handleIntersection, ANIMATION_CONFIG);

    // Observe all elements with animation classes
    const animatedElements = document.querySelectorAll('.fade-up, .reveal-scale');

    animatedElements.forEach(element => {
      // Only observe elements that don't already have 'visible' class
      if (!element.classList.contains('visible')) {
        observer.observe(element);
      }
    });

    return observer;
  };

  /**
   * Re-initialize observer for dynamically added content
   * Call this function after adding new animated elements to the DOM
   */
  window.mmolAnimations = {
    observer: null,

    init() {
      this.observer = initAnimationObserver();
    },

    refresh() {
      if (this.observer) {
        this.observer.disconnect();
      }
      this.observer = initAnimationObserver();
    },

    // Manually trigger animation for specific element
    trigger(element) {
      if (element && !prefersReducedMotion) {
        element.classList.add('visible');
      }
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.mmolAnimations.init();
    });
  } else {
    // DOM already loaded
    window.mmolAnimations.init();
  }

  // Re-initialize after Section Rendering API updates (Shopify-specific)
  document.addEventListener('shopify:section:load', () => {
    window.mmolAnimations.refresh();
  });

  document.addEventListener('shopify:section:reorder', () => {
    window.mmolAnimations.refresh();
  });

  /**
   * Header Scroll Effect
   * Add 'header--scrolled' class when user scrolls down
   * On homepage, start with transparent header
   */
  const initHeaderScrollEffect = () => {
    const header = document.querySelector('.header');
    if (!header) return;

    // Check if we're on the homepage
    const isHomepage = window.location.pathname === '/' || window.location.pathname === '';
    const scrollThreshold = 50; // Pixels before triggering

    // Set initial state - transparent on homepage
    if (isHomepage) {
      header.classList.add('header--transparent');
    }

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > scrollThreshold) {
        // Scrolled down
        header.classList.remove('header--transparent');
        header.classList.add('header--scrolled');
      } else {
        // At top
        header.classList.remove('header--scrolled');
        if (isHomepage) {
          header.classList.add('header--transparent');
        }
      }
    };

    // Throttle scroll event for performance
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    /**
     * Magnetic Button Effect
     * Buttons that subtly attract to the cursor
     */
    const initMagneticButtons = () => {
      const buttons = document.querySelectorAll('.magnetic-button');

      buttons.forEach(btn => {
        btn.addEventListener('mousemove', (e) => {
          const rect = btn.getBoundingClientRect();
          const x = e.clientX - rect.left - rect.width / 2;
          const y = e.clientY - rect.top - rect.height / 2;

          // Strength of the magnetic effect
          const strength = 20;

          btn.style.transform = `translate(${x / strength}px, ${y / strength}px)`;
        });

        btn.addEventListener('mouseleave', () => {
          btn.style.transform = 'translate(0, 0)';
        });
      });
    };

    /**
     * Parallax Effect
     * Smooth scroll-based movement for background elements
     */
    const initParallax = () => {
      const parallaxElements = document.querySelectorAll('[data-parallax-speed]');

      if (parallaxElements.length === 0) return;

      const handleParallax = () => {
        const scrolled = window.scrollY;

        parallaxElements.forEach(el => {
          const speed = parseFloat(el.getAttribute('data-parallax-speed')) || 0.1;
          const yPos = -(scrolled * speed);
          el.style.transform = `translateY(${yPos}px)`;
        });
      };

      window.addEventListener('scroll', () => {
        window.requestAnimationFrame(handleParallax);
      }, { passive: true });
    };

    // Initialize new effects
    initMagneticButtons();
    initParallax();

    // Check initial state
    handleScroll();
  };

  // Initialize header scroll effect
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeaderScrollEffect);
  } else {
    initHeaderScrollEffect();
  }

})();
